/**
 * The join nothing else performs: stored overrides + system signals → the
 * resolved rendering environment, stamped onto the document root.
 *
 * `@ff/ui` ships both halves and deliberately connects neither.
 * `a11y/flags.ts` is pure and takes `SystemAccessibilitySignals` as an
 * argument; `layout/textSize.ts` and `layout/motion.ts` know how to *read* two
 * of the three signals but have no access to the persisted overrides, because
 * `@ff/ui` has no storage port. The composition root is the only place that
 * holds both, so the join lives here — and because it lives in exactly one
 * place, no surface can re-derive the accessibility branch and get a different
 * answer from the one `tokens.css` is painting against.
 */

import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo } from "react";
import type {
  AccessibilityOverrides,
  AppearanceMode,
  ColorScheme,
  SystemOverridablePreference,
  TextSizePreference,
} from "@ff/domain";
import { DEFAULT_ACCESSIBILITY_OVERRIDES, resolveColorScheme } from "@ff/domain";
import { APPEARANCE_MODE } from "@ff/store";
import type { AccessibilityFlags, MatchMedia, MediaProbeResult } from "@ff/ui";
import {
  LayoutPreferencesProvider,
  accessibilityRootAttributes,
  probeAccessibilityTextSize,
  probeReducedMotion,
  resolveAccessibilityFlags,
  useMediaProbe,
} from "@ff/ui";
import type { PreferenceCodec } from "./preferences";
import {
  booleanPreference,
  stringUnionPreference,
  usePreference,
  usePreferenceDefinition,
} from "./preferences";

// ---------------------------------------------------------------------------
// Preference keys
// ---------------------------------------------------------------------------

/**
 * `appearance.mode` is a §3.1 entry, so its key, its default and its decoder
 * come from the table in `@ff/store` rather than being restated here — the
 * decoder in particular, because F-ST-06's "any unknown stored value resolves
 * to System" is a rule the domain already answers with `resolveAppearanceMode`
 * and a local string-union codec would be a second answer to it.
 *
 * The five accessibility keys are **not** in the §3.1 table — that table is the
 * navigation and onboarding persistence surface only — and no other packet has
 * declared them, so they are declared here in the same dotted grammar and
 * re-exported for the Settings packet to bind its controls to rather than
 * inventing a parallel set.
 */
export const APPEARANCE_MODE_KEY = APPEARANCE_MODE.key;

export const ACCESSIBILITY_PREFERENCE_KEYS = {
  textSize: "accessibility.textSize",
  increaseContrast: "accessibility.increaseContrast",
  differentiateWithoutColor: "accessibility.differentiateWithoutColor",
  buttonShapes: "accessibility.buttonShapes",
  reduceMotion: "accessibility.reduceMotion",
} as const satisfies Readonly<Record<keyof AccessibilityOverrides, string>>;

const TEXT_SIZE_VALUES: readonly TextSizePreference[] = ["system", "accessibility"];
const SYSTEM_OVERRIDABLE_VALUES: readonly SystemOverridablePreference[] = ["system", "on"];

// Module-level so `usePreference`'s memo key is stable; a codec rebuilt per
// render would decode on every render and defeat the memo entirely.
const TEXT_SIZE_CODEC: PreferenceCodec<TextSizePreference> = stringUnionPreference(
  TEXT_SIZE_VALUES,
  DEFAULT_ACCESSIBILITY_OVERRIDES.textSize,
);
const INCREASE_CONTRAST_CODEC: PreferenceCodec<SystemOverridablePreference> = stringUnionPreference(
  SYSTEM_OVERRIDABLE_VALUES,
  DEFAULT_ACCESSIBILITY_OVERRIDES.increaseContrast,
);
const REDUCE_MOTION_CODEC: PreferenceCodec<SystemOverridablePreference> = stringUnionPreference(
  SYSTEM_OVERRIDABLE_VALUES,
  DEFAULT_ACCESSIBILITY_OVERRIDES.reduceMotion,
);
const DIFFERENTIATE_CODEC = booleanPreference(
  DEFAULT_ACCESSIBILITY_OVERRIDES.differentiateWithoutColor,
);
const BUTTON_SHAPES_CODEC = booleanPreference(DEFAULT_ACCESSIBILITY_OVERRIDES.buttonShapes);

// ---------------------------------------------------------------------------
// System signals
// ---------------------------------------------------------------------------

export const INCREASE_CONTRAST_QUERY = "(prefers-contrast: more)";
export const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";

/**
 * `prefers-contrast: more` is the only one of the five accessibility settings
 * with a system signal that `@ff/ui` does not already probe, so it is probed
 * here rather than added to a package this packet does not own.
 */
function probeIncreaseContrast(matchMedia: MatchMedia): MediaProbeResult<boolean> {
  return {
    value: matchMedia(INCREASE_CONTRAST_QUERY).matches,
    queries: [INCREASE_CONTRAST_QUERY],
  };
}

function probeDarkScheme(matchMedia: MatchMedia): MediaProbeResult<ColorScheme> {
  return {
    value: matchMedia(DARK_SCHEME_QUERY).matches ? "dark" : "light",
    queries: [DARK_SCHEME_QUERY],
  };
}

// ---------------------------------------------------------------------------
// The resolved environment
// ---------------------------------------------------------------------------

export interface AppEnvironment {
  readonly overrides: AccessibilityOverrides;
  /** What the UI must render, after overrides and system signals are folded. */
  readonly flags: AccessibilityFlags;
  readonly appearanceMode: AppearanceMode;
  /** What the system reports, before the mode is applied. */
  readonly systemColorScheme: ColorScheme;
  /** What is actually on screen. */
  readonly colorScheme: ColorScheme;
  readonly setAppearanceMode: (mode: AppearanceMode) => void;
  readonly setOverride: <K extends keyof AccessibilityOverrides>(
    key: K,
    value: AccessibilityOverrides[K],
  ) => void;
}

const EnvironmentContext = createContext<AppEnvironment | null>(null);

export function useAppEnvironment(): AppEnvironment {
  const environment = useContext(EnvironmentContext);
  if (environment === null) {
    throw new Error(
      "useAppEnvironment() requires an enclosing <EnvironmentProvider>, placed by " +
        "<AppStateProvider>.",
    );
  }
  return environment;
}

/** The resolved flags on their own, for surfaces that need nothing else. */
export function useAccessibilityFlags(): AccessibilityFlags {
  return useAppEnvironment().flags;
}

export interface EnvironmentProviderProps {
  /**
   * The element the `data-ff-*` contract is written to. Defaults to the
   * document root, which is what `tokens.css` selects on; a test passes its own
   * element so two concurrent renders cannot fight over one document.
   */
  readonly rootElement?: HTMLElement | null;
  readonly children?: ReactNode;
}

function applyAttributes(
  element: HTMLElement,
  attributes: Readonly<Record<string, string | undefined>>,
): void {
  for (const [name, value] of Object.entries(attributes)) {
    // `undefined` means remove: an attribute set to the string "undefined"
    // keeps matching `[data-ff-contrast]` and would pin the treatment on.
    if (value === undefined) element.removeAttribute(name);
    else element.setAttribute(name, value);
  }
}

export function EnvironmentProvider({
  rootElement,
  children,
}: EnvironmentProviderProps): ReactNode {
  const [appearanceMode, setAppearanceMode] = usePreferenceDefinition(APPEARANCE_MODE);
  const [textSize, setTextSize] = usePreference(
    ACCESSIBILITY_PREFERENCE_KEYS.textSize,
    TEXT_SIZE_CODEC,
  );
  const [increaseContrast, setIncreaseContrast] = usePreference(
    ACCESSIBILITY_PREFERENCE_KEYS.increaseContrast,
    INCREASE_CONTRAST_CODEC,
  );
  const [differentiateWithoutColor, setDifferentiateWithoutColor] = usePreference(
    ACCESSIBILITY_PREFERENCE_KEYS.differentiateWithoutColor,
    DIFFERENTIATE_CODEC,
  );
  const [buttonShapes, setButtonShapes] = usePreference(
    ACCESSIBILITY_PREFERENCE_KEYS.buttonShapes,
    BUTTON_SHAPES_CODEC,
  );
  const [reduceMotion, setReduceMotion] = usePreference(
    ACCESSIBILITY_PREFERENCE_KEYS.reduceMotion,
    REDUCE_MOTION_CODEC,
  );

  const systemAccessibilityTextSize = useMediaProbe(probeAccessibilityTextSize, false);
  const systemIncreaseContrast = useMediaProbe(probeIncreaseContrast, false);
  const systemReduceMotion = useMediaProbe(probeReducedMotion, false);
  const systemColorScheme = useMediaProbe(probeDarkScheme, "light");

  const overrides = useMemo<AccessibilityOverrides>(
    () => ({
      textSize,
      increaseContrast,
      differentiateWithoutColor,
      buttonShapes,
      reduceMotion,
    }),
    [textSize, increaseContrast, differentiateWithoutColor, buttonShapes, reduceMotion],
  );

  const flags = useMemo(
    () =>
      resolveAccessibilityFlags(overrides, {
        accessibilityTextSize: systemAccessibilityTextSize,
        increaseContrast: systemIncreaseContrast,
        reduceMotion: systemReduceMotion,
      }),
    [overrides, systemAccessibilityTextSize, systemIncreaseContrast, systemReduceMotion],
  );

  const colorScheme = resolveColorScheme(appearanceMode, systemColorScheme);

  useEffect(() => {
    const element =
      rootElement ?? (typeof document === "undefined" ? null : document.documentElement);
    if (element === null) return;
    applyAttributes(element, {
      ...accessibilityRootAttributes(overrides),
      // "system" is the absence of a choice, so it is the absence of the
      // attribute — `tokens.css` then follows `prefers-color-scheme` on its own
      // rather than being pinned to whatever the system reported at mount.
      "data-ff-appearance":
        appearanceMode === "day" ? "day" : appearanceMode === "night" ? "night" : undefined,
    });
  }, [rootElement, overrides, appearanceMode]);

  const setOverride = useMemo(() => {
    const setters = {
      textSize: setTextSize,
      increaseContrast: setIncreaseContrast,
      differentiateWithoutColor: setDifferentiateWithoutColor,
      buttonShapes: setButtonShapes,
      reduceMotion: setReduceMotion,
    };
    return <K extends keyof AccessibilityOverrides>(
      key: K,
      value: AccessibilityOverrides[K],
    ): void => {
      // The map is keyed by the same union the value is drawn from, so each
      // setter receives exactly its own arm; the assertion narrows what the
      // indexed lookup widens to the union of all five setters.
      (setters[key] as (next: AccessibilityOverrides[K]) => void)(value);
    };
  }, [
    setTextSize,
    setIncreaseContrast,
    setDifferentiateWithoutColor,
    setButtonShapes,
    setReduceMotion,
  ]);

  const value = useMemo<AppEnvironment>(
    () => ({
      overrides,
      flags,
      appearanceMode,
      systemColorScheme,
      colorScheme,
      setAppearanceMode,
      setOverride,
    }),
    [
      overrides,
      flags,
      appearanceMode,
      systemColorScheme,
      colorScheme,
      setAppearanceMode,
      setOverride,
    ],
  );

  return (
    <EnvironmentContext.Provider value={value}>
      {/*
        `@ff/ui`'s layout hooks resolve the §8.1 branch from these two
        preferences plus their own probes. Feeding them the *overrides* rather
        than the resolved flags is deliberate: the hooks re-probe the system
        themselves, and handing them an already-resolved boolean would make a
        system change invisible until this provider happened to re-render.
      */}
      <LayoutPreferencesProvider textSize={textSize} reduceMotion={reduceMotion}>
        {children}
      </LayoutPreferencesProvider>
    </EnvironmentContext.Provider>
  );
}
